/**
 * RA Face Shape Guide — frame recommendations
 *
 * Tabs switch the face shape (card + product grid); the sort/filter dropdowns act on the
 * grid of the active shape. The "Face Shape" dropdown is a second face of the tabs — it
 * switches the shape and clears the frame filters, so the two never disagree and no
 * shape + facet combination comes back empty. Markup lives in
 * sections/ra-face-guide.liquid; this file only reads data-* hooks. Facets are stored on
 * each card as "|value|" lists (data-shape, data-material, data-color) so membership is a
 * substring test.
 */
(function () {
  'use strict';

  var SORTERS = {
    popularity: function (a, b) { return number(a.dataset.index) - number(b.dataset.index); },
    'price-asc': function (a, b) { return number(a.dataset.price) - number(b.dataset.price); },
    'price-desc': function (a, b) { return number(b.dataset.price) - number(a.dataset.price); },
    name: function (a, b) { return (a.dataset.name || '').localeCompare(b.dataset.name || ''); }
  };

  function number(value) {
    return parseInt(value, 10) || 0;
  }

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function hasFacet(card, key, value) {
    return (card.dataset[key] || '').indexOf('|' + value + '|') !== -1;
  }

  function initGuide(root) {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    var tabs = toArray(root.querySelectorAll('[data-guide-tab]'));
    var panels = toArray(root.querySelectorAll('[data-guide-panel]'));
    var grids = toArray(root.querySelectorAll('[data-guide-grid]'));
    var dropdowns = toArray(root.querySelectorAll('[data-guide-dropdown]'));
    var emptyMessage = root.querySelector('[data-guide-empty]');
    var filtersToggle = root.querySelector('[data-guide-filters-toggle]');
    var filterList = root.querySelector('[data-guide-filter-list]');
    if (!tabs.length || !grids.length) return;

    var state = { shape: '', sort: 'popularity', filters: { shape: '', material: '', color: '' } };

    function activeCards() {
      var grid = grids.filter(function (g) { return g.dataset.guideGrid === state.shape; })[0];
      return grid ? { grid: grid, cards: toArray(grid.querySelectorAll('[data-guide-product]')) } : { grid: null, cards: [] };
    }

    function matches(card) {
      return Object.keys(state.filters).every(function (key) {
        return !state.filters[key] || hasFacet(card, key, state.filters[key]);
      });
    }

    function applyCatalog() {
      var active = activeCards();
      if (!active.grid) return;
      var visible = [];
      active.cards.forEach(function (card) {
        var shown = matches(card);
        card.hidden = !shown;
        if (shown) visible.push(card);
      });
      visible.sort(SORTERS[state.sort] || SORTERS.popularity).forEach(function (card) {
        active.grid.appendChild(card);
      });
      if (emptyMessage) emptyMessage.hidden = visible.length > 0;
    }

    function selectOption(dropdown, option) {
      var key = dropdown.dataset.guideDropdown;
      var value = option.dataset.guideOption;
      var valueEl = dropdown.querySelector('[data-guide-dropdown-value]');
      if (key === 'sort') {
        state.sort = value;
      } else {
        state.filters[key] = value;
      }
      if (valueEl) valueEl.textContent = value ? option.textContent.trim() : '';
      dropdown.querySelectorAll('[data-guide-option]').forEach(function (other) {
        other.setAttribute('aria-selected', other === option ? 'true' : 'false');
      });
    }

    // The Face Shape dropdown mirrors the tabs: its value tracks the active shape.
    function syncFaceDropdown(shape) {
      var dropdown = dropdowns.filter(function (d) { return d.dataset.guideDropdown === 'face'; })[0];
      if (!dropdown) return;
      var valueEl = dropdown.querySelector('[data-guide-dropdown-value]');
      var selected = null;
      dropdown.querySelectorAll('[data-guide-option]').forEach(function (option) {
        var isSelected = option.dataset.guideOption === shape;
        option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        if (isSelected) selected = option;
      });
      if (valueEl) valueEl.textContent = selected ? selected.textContent.trim() : '';
    }

    // Choosing a face shape (tab or dropdown) starts the grid from scratch, so a shape and a
    // frame facet can never combine into an empty result.
    function resetFilters() {
      dropdowns.forEach(function (dropdown) {
        var key = dropdown.dataset.guideDropdown;
        if (key === 'sort' || key === 'face') return;
        var all = dropdown.querySelector('[data-guide-option=""]');
        if (all) selectOption(dropdown, all);
      });
    }

    // Filter values no frame of the active shape carries are hidden; a stale selection resets.
    function refreshOptions() {
      var cards = activeCards().cards;
      dropdowns.forEach(function (dropdown) {
        var key = dropdown.dataset.guideDropdown;
        if (key === 'sort' || key === 'face') return;
        dropdown.querySelectorAll('[data-guide-option]').forEach(function (option) {
          var value = option.dataset.guideOption;
          var available = value === '' || cards.some(function (card) { return hasFacet(card, key, value); });
          option.parentElement.hidden = !available;
          if (!available && state.filters[key] === value) {
            selectOption(dropdown, dropdown.querySelector('[data-guide-option=""]'));
          }
        });
      });
    }

    function setOpen(dropdown, open) {
      dropdown.querySelector('[data-guide-dropdown-toggle]').setAttribute('aria-expanded', open ? 'true' : 'false');
      dropdown.querySelector('[data-guide-dropdown-menu]').hidden = !open;
    }

    function closeAll(except) {
      dropdowns.forEach(function (dropdown) {
        if (dropdown !== except) setOpen(dropdown, false);
      });
    }

    function activate(key, updateHash) {
      state.shape = key;
      tabs.forEach(function (tab) {
        var selected = tab.dataset.guideTab === key;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });
      panels.forEach(function (panel) { panel.hidden = panel.dataset.guidePanel !== key; });
      grids.forEach(function (grid) { grid.hidden = grid.dataset.guideGrid !== key; });
      syncFaceDropdown(key);
      resetFilters();
      refreshOptions();
      applyCatalog();
      if (updateHash && window.history && window.history.replaceState) {
        window.history.replaceState(null, '', '#' + key);
      }
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        activate(tab.dataset.guideTab, true);
      });
      tab.addEventListener('keydown', function (event) {
        var step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        var next = tabs[(index + step + tabs.length) % tabs.length];
        next.focus();
        activate(next.dataset.guideTab, true);
      });
    });

    dropdowns.forEach(function (dropdown) {
      var toggle = dropdown.querySelector('[data-guide-dropdown-toggle]');
      var menu = dropdown.querySelector('[data-guide-dropdown-menu]');
      toggle.addEventListener('click', function () {
        var open = menu.hidden;
        closeAll(dropdown);
        setOpen(dropdown, open);
      });
      menu.querySelectorAll('[data-guide-option]').forEach(function (option) {
        option.addEventListener('click', function () {
          if (dropdown.dataset.guideDropdown === 'face') {
            setOpen(dropdown, false);
            activate(option.dataset.guideOption, true);
            return;
          }
          selectOption(dropdown, option);
          setOpen(dropdown, false);
          applyCatalog();
        });
      });
    });

    document.addEventListener('click', function (event) {
      if (!dropdowns.some(function (dropdown) { return dropdown.contains(event.target); })) closeAll();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeAll();
    });

    if (filtersToggle && filterList) {
      filtersToggle.addEventListener('click', function () {
        var open = filterList.classList.toggle('is-open');
        filtersToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    var fromHash = window.location.hash.replace('#', '');
    var known = tabs.some(function (tab) { return tab.dataset.guideTab === fromHash; });
    activate(known ? fromHash : root.dataset.defaultShape || tabs[0].dataset.guideTab, false);
  }

  function boot() {
    document.querySelectorAll('[data-face-guide]').forEach(initGuide);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || typeof event.target.querySelector !== 'function') return;
    var root = event.target.querySelector('[data-face-guide]');
    if (root) {
      root.dataset.ready = '';
      initGuide(root);
      // Auglio scans for try-on buttons once, at page load; the theme editor re-renders the
      // section after that, so the new pills need a rescan to stay clickable.
      if (typeof window.virtooalRefreshBtns === 'function') window.virtooalRefreshBtns();
    }
  });
})();
